/**
 * Config store — holds server-injected configuration.
 * Populated once at boot from the JSON blob in the PHP shell.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { BrandColor } from '../../shared/brandPalette';
import { currencyFromLegacy, defaultCurrency, type CurrencyContext } from '../../shared/money';

export interface ServerConfig {
  siteUrl: string;
  /**
   * Addon data that affects price and must reach the cart, keyed by addon
   * (e.g. Smart Configurator selections). Sent as `pricing_context` with
   * every price quote and add-to-cart; the server re-validates it.
   */
  pricingContext?: Record<string, unknown>;
  siteTitle?: string;
  siteLogo?: string;
  /** Shop home page: the logo / store name in the designer's top bar link here. */
  siteHomeUrl?: string;
  textStyleCards?: Array<Record<string, unknown>>;
  /** Published, admin-ordered colors available to shopper design controls. */
  brandPalette?: BrandColor[];
  veyopUrl: string;
  pluginBaseUrl?: string;
  apiBaseUrl?: string;
  uploadBaseUrl?: string;
  backgroundRemoverDataUrl?: string;
  backgroundRemoverUseWorker?: boolean;
  thumbUrl: string;
  domain: string;
  userId: string;
  parentId: number;
  productId: string;
  printType: string;
  /** 1 = show printing selector on designer (catalog setting). */
  allowShowPrintingType?: number;
  printingTypes: PrintingTypeConfig[];
  currencySymbol: string;
  currencyPosition: string;
  currency: CurrencyContext;
  langActive: string;
  uploadMax: number;
  minOrder: number;
  maxOrder: number;
  variationId: string;
  variationAttributes: string;
  designId: string;
  designerId: string;
  shareToken: string;
  cartId: string;
  orderItemId: string;
  orderAccess: string;
  initialColor: string;
  /** Legacy storefront attribute selection passed through ?options=. */
  initialOptions?: string;
  /** From URL qty=, clamped to min/max order */
  initialQuantity?: number;
  isLightbox: boolean;
  mode?: 'storefront' | 'template-admin';
  templateId?: number;
  /** Published assigned-template state used by storefront archive deep links. */
  initialDesignTemplate?: InitialDesignTemplateConfig;
  csrfToken: string;
  apiNonce?: string;
  theme?: ServerThemeConfig;
  settings: Record<string, unknown>;
  lang: Record<string, string>;
  items: ItemsConfig;
  printsConfirmColors: string[];
  product: {
    id: string;
    title: string;
    image: string;
    price: string | number;
    salePrice: string | number;
    regularPrice?: string | number;
    description?: string;
    shortDescription?: string;
    sku?: string;
    stockStatus?: string;
    gallery?: string[];
    attribute: unknown;
    attributes: unknown;
    /** Sanitized catalog size-chart content. */
    sizeChart?: string;
    /** Explicit rendering mode; legacy products default to sanitized HTML. */
    sizeChartFormat?: 'text' | 'html';
  };
}

export interface InitialDesignTemplateConfig {
  id: number;
  updatedAt?: string;
  designerState: {
    items: Record<string, unknown[]>;
    views: string[];
    activeView: string;
    metadata?: Record<string, unknown>;
  };
}

export interface ServerThemeConfig {
  slug: string;
  title: string;
  description?: string;
  version?: string;
  baseUrl?: string;
  options?: Record<string, unknown>;
  settings?: Record<string, unknown>;
}

export interface PrintingTypeConfig {
  id: number;
  title: string;
  printing_code: string;
  price_type: string;
  short_description?: string;
  description?: string;
  price_extra: number;
  values?: {
    base_price?: number;
    setup_fee?: number;
    suggested_price?: number;
    cost_note?: string;
    tiers?: Array<{ min_qty: number; unit_price: number }>;
    [key: string]: unknown;
  };
  view?: number;
}

export interface ItemsDesignEntry {
  color: string;
  title: string;
  /** Optional swatch texture image URL (catalog product color). */
  texture?: string;
  front: string;
  back: string;
  left: string;
  right: string;
  /** Addon-registered views arrive as extra per-view SVG string keys. */
  [viewKey: string]: string | undefined;
}

/** Ordered view definition emitted by api-config.php. */
export interface ItemsViewDef {
  id: string;
  label: string;
  priority: number;
  core?: boolean;
}

/** Per-view garment stack from api-config (same order as admin: bottom → top). */
export interface GarmentLayerConfig {
  url: string;
  top: number;
  left: number;
  width: number;
  height: number;
  z: number;
  tintable?: boolean;
  tintMode?: 'multiply' | 'recolor' | 'none';
  colorSlot?: number;
  locked?: boolean;
}

export interface ItemsConfig {
  design: ItemsDesignEntry[];
  /** Ordered view defs (core + addon + data-found); absent in older cached configs. */
  views?: ItemsViewDef[];
  area: Record<string, string>;
  params: Record<string, string>;
  boxWidth?: number;
  boxHeight?: number;
  /** New uploads center-cover the active print area while preserving aspect ratio. */
  artFullArea?: boolean;
  viewImages?: Record<string, string>;
  /** Optional per-view layered mockups; when present for a view, DesignView stacks these instead of a single URL. */
  garmentLayers?: Record<string, GarmentLayerConfig[]>;
  /** When true, tintable garment layers use the active catalog color at runtime. */
  colorTintEnabled?: boolean;
  /** When true, the storefront replaces catalog swatches with one arbitrary HEX picker. */
  customColorEnabled?: boolean;
  /** Product print-readiness preset and per-view overrides. */
  /** Owned by the preflight addon; core only needs to pass this through untyped. */
  preflightProfile?: Record<string, unknown>;
  /** Addon-owned, normalized per-view embroidery settings. */
  embroideryProfile?: unknown;
}

export const useConfigStore = defineStore('config', () => {
  const data = ref<ServerConfig>({
    siteUrl: '',
    siteTitle: '',
    siteLogo: '',
    siteHomeUrl: '',
    textStyleCards: [],
    brandPalette: undefined,
    veyopUrl: '',
    pluginBaseUrl: '',
    apiBaseUrl: '',
    uploadBaseUrl: '',
    backgroundRemoverDataUrl: '',
    backgroundRemoverUseWorker: false,
    thumbUrl: '',
    domain: '',
    userId: '0',
    parentId: 0,
    productId: '',
    printType: '',
    allowShowPrintingType: 1,
    printingTypes: [],
    currencySymbol: '$',
    currencyPosition: 'left',
    currency: { ...defaultCurrency },
    langActive: '',
    uploadMax: 10,
    minOrder: 1,
    maxOrder: 9999,
    variationId: '',
    variationAttributes: '',
    designId: '',
    designerId: '',
    shareToken: '',
    cartId: '',
    orderItemId: '',
    orderAccess: '',
    initialColor: '',
    initialOptions: '',
    isLightbox: false,
    mode: 'storefront',
    templateId: 0,
    csrfToken: '',
    theme: {
      slug: 'default',
      title: 'Default Designer',
      description: '',
      version: '',
      baseUrl: '',
      options: {},
      settings: {},
    },
    settings: {},
    lang: {},
    items: { design: [], area: {}, params: {}, boxWidth: 500, boxHeight: 500 },
    printsConfirmColors: [],
    product: { id: '', title: '', image: '', price: '', salePrice: '', attribute: null, attributes: null },
  });

  const isLoggedIn = computed(() => data.value.userId !== '0' && data.value.userId !== '');

  function init(raw: Record<string, unknown>) {
    const merged = { ...data.value, ...raw } as ServerConfig;
    merged.currency = currencyFromLegacy(merged.currency, merged.currencySymbol, merged.currencyPosition);
    merged.currencySymbol = merged.currency.symbol;
    merged.currencyPosition = merged.currency.position;
    data.value = merged;
  }

  /**
   * Validate required config fields and return a list of missing/invalid keys.
   * An empty array means the config is valid.
   */
  function validateConfig(): string[] {
    const d = data.value;
    const errors: string[] = [];
    if (!d.siteUrl) errors.push('siteUrl');
    if (!d.veyopUrl) errors.push('veyopUrl');
    if (!d.productId) errors.push('productId');
    if (!d.csrfToken) errors.push('csrfToken');
    if (!d.items || !Array.isArray(d.items.design)) errors.push('items.design');
    if (!d.product || !d.product.id) errors.push('product.id');
    return errors;
  }

  /** Translate a key using the lang map */
  function t(key: string, fallback?: string): string {
    return data.value.lang[key] ?? fallback ?? key;
  }

  return { data, isLoggedIn, init, validateConfig, t };
});
